import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from '../../../../utils/helpers';
import './SelectedTeamGolfCompleted.css'; // Import Completed CSS

const SelectedTeamGolfCompleted = ({ team, contest }) => {

  // --- Hooks must be called before conditional returns ---

  // Player Data Map (Score, Thru, Salary, Status needed)
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      map[player.name] = {
        score: player.sportSpecificData?.score ?? null,
        thru: player.sportSpecificData?.thru ?? null,
        salary: player.salary,
        status: player.sportSpecificData?.status ?? null
        // finish not needed for completed view logic
      };
    });
    return map;
  }, [contest.players]);

  // Calculate Total Team Score
  const totalTeamScore = useMemo(() => {
    if (!team || !team.team) return null;
    let total = 0;
    let playersContributing = 0;

    const captainData = playerDataMap[team.team.captain];
    if (captainData && typeof captainData.score === 'number') {
      total += captainData.score; 
      playersContributing++;
    }

    team.team.flexPlayers.forEach(player => {
      const playerData = playerDataMap[player];
      if (playerData && typeof playerData.score === 'number') {
        total += playerData.score;
        playersContributing++;
      }
    });
    // Use null if no players scored, otherwise total
    return playersContributing > 0 ? total : null; 
  }, [playerDataMap, team]);

  // --- Conditional return ---
  if (!team) return null;

  // --- Logic that depends on team ---

  // Format final Thru status for display in completed view
  const formatFinalThru = (status, thru) => {
    if (status === 'cut') return 'CUT';
    if (thru === 18 || thru === 'F') return 'F'; // Finished 18 holes
    // If neither cut nor finished, display '-' or actual thru if needed
    // For completed, usually implies WD or other status, defaulting to '-'
    return '-'; 
  };

  // Format score for display
  const formatScore = (score) => {
    if (score === null) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : score.toString();
  };

  // Get salaries for display
  const captainSalary = playerDataMap[team.team.captain]?.salary || 0;
  const flexPlayerSalaries = team.team.flexPlayers.map(
    player => playerDataMap[player]?.salary || 0
  );

  return (
    <div className="selected-team-container-golf-completed"> {/* UPDATED CLASS */} 
      <div className="selected-team-header">
        <h2>{team.entryName || team.username}'s Final Team</h2> {/* Updated Header */} 
      </div>
      <div className="selected-team-content">
        <table className="selected-team-table">
          <thead>
            {/* Removed classes from th for simplicity, alignment handled by CSS */}
            <tr><th>Pos</th><th>Name</th><th className="show-on-desktop">Salary</th><th>Thru</th><th>Score</th></tr>
          </thead>
          <tbody>
            {(() => { // Captain Row
              const captainName = team.team.captain;
              const captainData = playerDataMap[captainName];
              const captainThru = captainData?.thru;
              const captainScore = captainData?.score ?? null;
              const captainStatus = captainData?.status;
              // Removed isCaptainCut and captainStatusClass
              
              return (
                // Removed status class from tr
                <tr>
                  <td><span className="position-label position-label--cpt">CPT*</span></td>
                  {/* Removed status class from td */}
                  <td className="captain-cell">{captainName}</td>
                  <td className="show-on-desktop">${formatNumber(captainSalary)}</td>
                  {/* Use formatFinalThru */}
                  <td className="thru-value">{formatFinalThru(captainStatus, captainThru)}</td>
                  <td className="score-value">{formatScore(captainScore)}</td>
                </tr>
              );
            })()}
            {team.team.flexPlayers.map((player, index) => { // Flex Player Rows
              const playerData = playerDataMap[player];
              const playerThru = playerData?.thru;
              const playerScore = playerData?.score ?? null;
              const playerStatus = playerData?.status;
              const playerSalary = flexPlayerSalaries[index];
              // Removed isPlayerCut and playerStatusClass

              return (
                // Removed status class from tr
                <tr key={index}>
                  <td><span className="position-label">FLEX</span></td>
                  {/* Removed status class from td */}
                  <td className="flex-cell">{player}</td>
                  <td className="show-on-desktop">${formatNumber(playerSalary)}</td>
                  {/* Use formatFinalThru */}
                  <td className="thru-value">{formatFinalThru(playerStatus, playerThru)}</td>
                  <td className="score-value">{formatScore(playerScore)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td className="totals-label" colSpan="2">Totals:</td>
              <td className="show-on-desktop totals-value">${formatNumber(team.teamSalary)}</td>
              {/* Empty cell under Thru */}
              <td></td> 
              <td className="totals-value">{formatScore(totalTeamScore)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// Updated PropTypes
SelectedTeamGolfCompleted.propTypes = {
  team: PropTypes.shape({
    _id: PropTypes.string, // Include _id if available and needed
    username: PropTypes.string,
    entryName: PropTypes.string,
    team: PropTypes.shape({
      captain: PropTypes.string.isRequired,
      flexPlayers: PropTypes.arrayOf(PropTypes.string).isRequired
    }).isRequired,
    teamSalary: PropTypes.number
  }).isRequired,
  contest: PropTypes.shape({
     players: PropTypes.arrayOf(PropTypes.shape({
       name: PropTypes.string.isRequired,
       salary: PropTypes.number,
       sportSpecificData: PropTypes.shape({ 
         score: PropTypes.number,
         thru: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), 
         status: PropTypes.string // Need status for 'CUT' display
       })
     })).isRequired
   }).isRequired
};

export default SelectedTeamGolfCompleted;
